/**
 * PRD.264 — Used Supplies service.
 *
 * Imperative operations against the local PowerSync DB that record supply usage
 * (deducting stock), reverse usage (restoring stock), and mark a used supply as
 * billed. Pure validation/billing/reversal logic lives in `@helios/data-access`
 * (supplies-logic) and is unit-tested there.
 *
 * Usage, stock deduction, and billing are separate: posting a used supply
 * deducts stock but never bills the patient. Billing pulls ready_for_billing
 * rows later. Stock is never deducted twice.
 */

import {
  canReverseUsedSupply,
  initialUsedSupplyBillingStatus,
  isSellable,
  validateUsedSupply,
} from '@helios/data-access';

interface PowerSyncLike {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
  getAll: <T>(sql: string, params?: unknown[]) => Promise<T[]>;
}

export interface CreateUsedSupplyInput {
  patientId: string;
  clinicId?: string | null;
  visitId?: string | null;
  encounterId?: string | null;
  inventoryItemId: string;
  inventoryBatchId?: string | null;
  quantity: number;
  usedBy?: string | null;
  notes?: string | null;
  clientReference?: string | null;
}

export interface UsedSupplyResult {
  ok: boolean;
  error?: string;
  usedSupplyId?: string;
  duplicate?: boolean;
}

interface SupplyItemRow {
  id: string;
  org_id: string | null;
  item_type: string | null;
  item_name: string | null;
  sku: string | null;
  supply_usage_type: string | null;
  is_sellable: number | null;
  track_stock: number | null;
  batch_tracked: number | null;
  inventory_unit: string | null;
  selling_price: number | null;
  cost_price: number | null;
}

async function batchAvailable(db: PowerSyncLike, batchId: string): Promise<number> {
  const rows = await db.getAll<{ avail: number | null }>(
    `SELECT COALESCE(SUM(COALESCE(quantity_in, 0)), 0) - COALESCE(SUM(COALESCE(quantity_out, 0)), 0) AS avail
       FROM stock_movements WHERE inventory_batch_id = ?`,
    [batchId],
  );
  return rows[0]?.avail ?? 0;
}

async function itemAvailable(db: PowerSyncLike, itemId: string): Promise<number> {
  const rows = await db.getAll<{ avail: number | null }>(
    `SELECT COALESCE(SUM(COALESCE(quantity_in, 0)), 0) - COALESCE(SUM(COALESCE(quantity_out, 0)), 0) AS avail
       FROM stock_movements WHERE inventory_item_id = ?`,
    [itemId],
  );
  return rows[0]?.avail ?? 0;
}

/**
 * Record a supply as used (PRD.264 §13): validate the supply, quantity, and
 * batch; deduct stock when track_stock; write the used_supplies ledger row with
 * snapshots; set billing_status from sellability.
 */
export async function createUsedSupply(params: {
  db: PowerSyncLike;
  input: CreateUsedSupplyInput;
  userId?: string | null;
}): Promise<UsedSupplyResult> {
  const { db, input, userId = null } = params;
  const quantity = Number(input.quantity);

  const itemRows = await db.getAll<SupplyItemRow>(
    `SELECT id, org_id, item_type, item_name, sku, supply_usage_type, is_sellable,
            track_stock, batch_tracked, inventory_unit, selling_price, cost_price
       FROM inventory_items WHERE id = ?`,
    [input.inventoryItemId],
  );
  const item = itemRows[0];
  if (!item) return { ok: false, error: 'Supply item not found. Please refresh.' };
  if (item.item_type !== 'supply') {
    return { ok: false, error: 'This item is not a supply.' };
  }
  const orgId = item.org_id;
  if (!orgId) return { ok: false, error: 'Missing organization context.' };

  const batchTracked = item.batch_tracked === 1;
  const trackStock = item.track_stock === 1;
  const batchId = input.inventoryBatchId ?? null;

  // Idempotency for offline retries.
  if (input.clientReference) {
    const dup = await db.getAll<{ id: string }>(
      `SELECT id FROM used_supplies WHERE org_id = ? AND client_reference = ? AND deleted_at IS NULL LIMIT 1`,
      [orgId, input.clientReference],
    );
    if (dup[0]) return { ok: true, usedSupplyId: dup[0].id, duplicate: true };
  }

  // Availability guard (PRD.264 §4, §13). A batch is required only for
  // batch-tracked supplies (validated below); non-batch stock-tracked supplies
  // are guarded against over-deduction at the item level. Non-stock supplies
  // (available = null) skip the check and never deduct.
  let batchNo: string | null = null;
  let expiry: string | null = null;
  let available: number | null = null;
  if (batchId) {
    const bRows = await db.getAll<{ batch_number: string | null; expiry_date: string | null }>(
      `SELECT batch_number, expiry_date FROM inventory_batches WHERE id = ?`,
      [batchId],
    );
    batchNo = bRows[0]?.batch_number ?? null;
    expiry = bRows[0]?.expiry_date ?? null;
    available = await batchAvailable(db, batchId);
  } else if (trackStock) {
    available = await itemAvailable(db, item.id);
  }

  const validationError = validateUsedSupply({
    quantity,
    batchTracked,
    batchId,
    available,
  });
  if (validationError) return { ok: false, error: validationError };

  const sellable = isSellable(item.is_sellable);
  const billingStatus = initialUsedSupplyBillingStatus(sellable);
  const now = new Date().toISOString();
  const today = now.slice(0, 10);
  const usedSupplyId = crypto.randomUUID();

  // Deduct stock (PRD.264 §15) — one outbound movement, linked to the used
  // supply so Stock Card and reversal can trace it.
  let stockMovementId: string | null = null;
  if (trackStock) {
    stockMovementId = crypto.randomUUID();
    await db.execute(
      `INSERT INTO stock_movements (
         id, org_id, inventory_item_id, inventory_batch_id,
         source_type, source_id, source_line_id,
         movement_type, quantity_in, quantity_out, unit,
         movement_date, remarks, created_by, created_at, updated_at
       ) VALUES (?, ?, ?, ?, 'used_supply', ?, NULL, 'used_supply', 0, ?, ?, ?, ?, ?, ?, ?)`,
      [
        stockMovementId,
        orgId,
        item.id,
        batchId,
        usedSupplyId,
        quantity,
        item.inventory_unit ?? null,
        today,
        'Used supply',
        userId,
        now,
        now,
      ],
    );
  }

  await db.execute(
    `INSERT INTO used_supplies (
       id, org_id, clinic_id, patient_id, visit_id, encounter_id,
       inventory_item_id, inventory_batch_id, stock_movement_id, invoice_id, invoice_item_id,
       item_name_snapshot, item_code_snapshot, supply_usage_type_snapshot, is_sellable_snapshot,
       quantity, unit_snapshot, batch_no_snapshot, expiry_date_snapshot,
       unit_price_snapshot, cost_snapshot,
       usage_status, billing_status, used_at, used_by, notes,
       client_reference, created_at, updated_at, deleted_at
     ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, NULL, NULL, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'posted', ?, ?, ?, ?, ?, ?, ?, NULL)`,
    [
      usedSupplyId,
      orgId,
      input.clinicId ?? null,
      input.patientId,
      input.visitId ?? null,
      input.encounterId ?? null,
      item.id,
      batchId,
      stockMovementId,
      item.item_name ?? 'Supply',
      item.sku ?? null,
      item.supply_usage_type ?? null,
      sellable ? 1 : 0,
      quantity,
      item.inventory_unit ?? null,
      batchNo,
      expiry,
      item.selling_price ?? null,
      item.cost_price ?? null,
      billingStatus,
      now,
      input.usedBy ?? userId,
      input.notes?.trim() || null,
      input.clientReference ?? null,
      now,
      now,
    ],
  );

  return { ok: true, usedSupplyId };
}

export interface ReverseUsedSupplyResult {
  ok: boolean;
  error?: string;
}

/**
 * Reverse a posted used supply (PRD.264 §18): restore stock via a compensating
 * movement and mark the usage reversed. Blocked once billed.
 */
export async function reverseUsedSupply(params: {
  db: PowerSyncLike;
  usedSupplyId: string;
  reason: string;
  userId?: string | null;
}): Promise<ReverseUsedSupplyResult> {
  const { db, usedSupplyId, reason, userId = null } = params;
  if (!reason.trim()) return { ok: false, error: 'A reason is required to reverse a used supply.' };

  const rows = await db.getAll<{
    id: string;
    org_id: string | null;
    inventory_item_id: string | null;
    inventory_batch_id: string | null;
    stock_movement_id: string | null;
    quantity: number | null;
    unit_snapshot: string | null;
    usage_status: string | null;
    billing_status: string | null;
    deleted_at: string | null;
  }>(
    `SELECT id, org_id, inventory_item_id, inventory_batch_id, stock_movement_id, quantity,
            unit_snapshot, usage_status, billing_status, deleted_at
       FROM used_supplies WHERE id = ?`,
    [usedSupplyId],
  );
  const u = rows[0];
  if (!u) return { ok: false, error: 'Used supply not found.' };

  const guard = canReverseUsedSupply({
    usageStatus: u.usage_status,
    billingStatus: u.billing_status,
    deletedAt: u.deleted_at,
  });
  if (!guard.ok) return { ok: false, error: guard.error };

  const now = new Date().toISOString();

  // Restore stock if it was deducted.
  if (u.stock_movement_id && u.inventory_item_id && u.org_id) {
    await db.execute(
      `INSERT INTO stock_movements (
         id, org_id, inventory_item_id, inventory_batch_id,
         source_type, source_id, source_line_id,
         movement_type, quantity_in, quantity_out, unit,
         movement_date, remarks, created_by, created_at, updated_at
       ) VALUES (?, ?, ?, ?, 'used_supply_reversal', ?, ?, 'used_supply_reversal', ?, 0, ?, ?, ?, ?, ?, ?)`,
      [
        crypto.randomUUID(),
        u.org_id,
        u.inventory_item_id,
        u.inventory_batch_id ?? null,
        usedSupplyId,
        u.stock_movement_id,
        u.quantity ?? 0,
        u.unit_snapshot ?? null,
        now.slice(0, 10),
        'Used supply reversal',
        userId,
        now,
        now,
      ],
    );
  }

  await db.execute(
    `UPDATE used_supplies
        SET usage_status = 'reversed', reversed_at = ?, reversed_by = ?, reverse_reason = ?, updated_at = ?
      WHERE id = ?`,
    [now, userId, reason.trim(), now, usedSupplyId],
  );

  return { ok: true };
}

/**
 * Link a used supply to the invoice line created for it and mark it billed
 * (PRD.264 §16). Does NOT touch stock — it was already deducted at usage time.
 */
export async function markUsedSupplyBilled(params: {
  db: PowerSyncLike;
  usedSupplyId: string;
  invoiceId: string;
  invoiceItemId: string;
}): Promise<void> {
  const { db, usedSupplyId, invoiceId, invoiceItemId } = params;
  await db.execute(
    `UPDATE used_supplies
        SET billing_status = 'billed', invoice_id = ?, invoice_item_id = ?, updated_at = ?
      WHERE id = ?`,
    [invoiceId, invoiceItemId, new Date().toISOString(), usedSupplyId],
  );
}
